/* ============================================================
   HEIDI — Demande de devis

   MODÈLE : la demande de devis a sa PROPRE liste, distincte du panier Shopify.

   C'est un revirement assumé, décidé par Yann le 30/07/2026. Le modèle précédent
   — « un seul panier, deux sorties » — faisait qu'un clic sur « Demander un devis »
   ajoutait réellement l'article au panier de commande. Trois conséquences, toutes
   constatées dans le code :
     — l'acheteur qui demandait un devis se retrouvait avec l'article à payer ;
     — l'étape « panier » du tunnel devenait ininterprétable, une intention de
       négocier gonflant l'abandon de panier ;
     — un échec d'ajout redirigeait exactement comme un succès, donc l'acheteur
       arrivait sur la page devis sans son article et sans aucun message.

   CE QUI NE CHANGE PAS : la sortie. L'équipe crée toujours une commande brouillon
   Shopify avec les prix négociés, et c'est de là que vient la mesurabilité — pas
   du transit par le panier. Ne pas confondre les deux, la note de projet a déjà
   été mal relue sur ce point.

   POURQUOI localStorage. Shopify n'a qu'UN panier : pas de second panier natif,
   et `cartCreate` de l'API Storefront exigerait un jeton public, interdit dans un
   thème dont le code est publiquement lisible. Les propriétés de ligne seraient un
   faux-semblant — la ligne resterait comptée et facturable au checkout, ce qui est
   précisément l'endroit interdit pour une demande de remise. Les metafields client
   seraient la seule solution multi-appareils, mais exigent une écriture Admin que
   nous n'avons pas.

   LIMITE ASSUMÉE, à dire à l'acheteur le jour où ça se justifie : la liste vit
   dans le navigateur. Elle ne suit pas d'un appareil à l'autre et disparaît si le
   stockage local est vidé.

   Aucune dépendance externe.
   ============================================================ */

(function () {
  'use strict';

  var CONFIG = (function () {
    var el = document.getElementById('hs-quote-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  })();

  if (!CONFIG) return;

  /* ------------------------------------------------------------------------- *
   * LE FORMAT MONÉTAIRE EST CELUI DE LA BOUTIQUE, PAS CELUI DU NAVIGATEUR.
   *
   * Corrigé le 02/08/2026. Cette fonction employait
   * `Intl.NumberFormat(locale, {style:'currency'})`, qui rend « 1 718,00 CHF » en
   * français. Or le prix unitaire de chaque ligne est rendu par Liquid `| money`,
   * qui suit le réglage de la boutique et donne « CHF 1,718.00 ». Séparateur
   * décimal, séparateur de milliers et place du symbole divergeaient donc SUR LA
   * MÊME LIGNE — le total de ligne dans un format, le prix unitaire dans l'autre.
   *
   * On applique désormais `shop.money_format`, le même gabarit que le filtre
   * Liquid, passé par la configuration. Les quatre marqueurs de Shopify sont
   * couverts ; `amount` est la seule forme utilisée par une boutique en CHF, les
   * trois autres existent pour les boutiques qui changeraient de format sans
   * qu'on ait à repasser ici.
   * ------------------------------------------------------------------------- */
  var money = (function () {
    var GABARIT = CONFIG.moneyFormat || '{{amount}}';

    function grouper(nombre, decimales, sepMillier, sepDecimal) {
      var n = (nombre || 0) / 100;
      var fixe = Math.abs(n).toFixed(decimales);
      var parts = fixe.split('.');
      var entier = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMillier);
      var reste = parts[1] ? sepDecimal + parts[1] : '';
      return (n < 0 ? '-' : '') + entier + reste;
    }

    return function (cents) {
      var v = cents || 0;
      return GABARIT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, nom) {
        switch (nom) {
          case 'amount_no_decimals':              return grouper(v, 0, ',', '.');
          case 'amount_with_comma_separator':     return grouper(v, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return grouper(v, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return grouper(v, 2, '’', '.');
          default:                                 return grouper(v, 2, ',', '.');
        }
      });
    };
  })();

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  /* ==========================================================
     1. LE MAGASIN

     Clé versionnée, contrairement aux cinq clés d'Ella qui ne le sont pas — et
     c'est exactement pourquoi le code de la liste d'envie doit filtrer les nuls
     après lecture. Une version permet de migrer ou de jeter proprement.

     On stocke le strict nécessaire : identifiant de VARIANTE, handle, quantité,
     horodatage. Pas le titre (traduit par Translate & Adapt), pas le prix
     (converti côté serveur, donc figé serait faux dès un changement de devise),
     pas l'image ni le SKU. Tout cela est rendu par le serveur à l'affichage.

     L'identifiant est celui de la variante et non du produit : un écrou type DA
     existe en plusieurs diamètres, et un devis B2B sur le mauvais diamètre ne
     vaut rien. Le handle reste nécessaire pour le rendu, qui passe par
     `/products/<handle>?variant=<id>&view=hs_quote_line`.

     RECTIFICATIF 03/10/2026 : la route storefront `/variants/<id>` EXISTE — vérifié
     sur heidi.shop, elle redirige vers `/products/<handle>?variant=<id>`, préfixe de
     langue compris. Elle ne sait pas porter `view=`, d'où le handle conservé, mais
     elle sert de source de vérité pour RÉPARER un handle faux (voir `resoudreHandle`).
     ========================================================== */

  var KEY = 'hs_quote_v1';

  var Store = {
    read: function () {
      var vide = { v: 1, items: [] };
      var raw;
      try { raw = window.localStorage.getItem(KEY); } catch (e) { return vide; }
      if (!raw) return vide;

      var data;
      try { data = JSON.parse(raw); } catch (e) { return vide; }
      if (!data || data.v !== 1 || !Array.isArray(data.items)) return vide;

      /* Filtrage défensif : une entrée sans identifiant ou sans handle ne peut pas
         être rendue. Mieux vaut la jeter en silence que casser toute la liste. */
      data.items = data.items.filter(function (it) {
        return it && it.id && it.h && (parseInt(it.q, 10) || 0) > 0;
      });
      return data;
    },

    write: function (data) {
      data.v = 1;
      try {
        window.localStorage.setItem(KEY, JSON.stringify(data));
      } catch (e) {
        /* Quota dépassé ou stockage refusé — navigation privée sur certains
           navigateurs. On ne casse rien : la liste reste celle de la session. */
        return false;
      }
      Badge.refresh();
      return true;
    },

    add: function (variantId, handle, quantity, stamp) {
      var id = String(variantId);
      var qty = Math.max(1, parseInt(quantity, 10) || 1);
      var data = this.read();
      var existant = null;

      for (var i = 0; i < data.items.length; i++) {
        if (String(data.items[i].id) === id) { existant = data.items[i]; break; }
      }

      if (existant) {
        existant.q = (parseInt(existant.q, 10) || 0) + qty;
        if (handle) existant.h = handle;
      } else {
        data.items.push({ id: id, h: handle, q: qty, t: stamp || null });
      }
      return this.write(data);
    },

    setQty: function (variantId, quantity) {
      var id = String(variantId);
      var qty = parseInt(quantity, 10) || 0;
      var data = this.read();
      if (qty <= 0) return this.remove(variantId);
      data.items.forEach(function (it) {
        if (String(it.id) === id) it.q = qty;
      });
      return this.write(data);
    },

    /* Corrige le handle d'une entrée sans toucher à sa quantité. Sert à la
       réparation des listes enregistrées avant le correctif du 03/10/2026. */
    setHandle: function (variantId, handle) {
      if (!handle) return false;
      var id = String(variantId);
      var data = this.read();
      data.items.forEach(function (it) {
        if (String(it.id) === id) it.h = handle;
      });
      return this.write(data);
    },

    remove: function (variantId) {
      var id = String(variantId);
      var data = this.read();
      data.items = data.items.filter(function (it) { return String(it.id) !== id; });
      return this.write(data);
    },

    clear: function () {
      try { window.localStorage.removeItem(KEY); } catch (e) { /* rien à faire */ }
      Badge.refresh();
    },

    /* Somme des quantités, et non nombre de lignes : c'est ce que compte la
       pastille du panier juste à côté, et deux pastilles voisines qui comptent
       différemment sont un piège à contresens. */
    count: function () {
      return this.read().items.reduce(function (n, it) {
        return n + (parseInt(it.q, 10) || 0);
      }, 0);
    }
  };

  /* SURFACE PUBLIQUE, VOLONTAIREMENT ÉTROITE — 07/08/2026.

     La page « Commande rapide » doit pouvoir verser sa liste dans la demande de devis.
     Ce fichier est une IIFE fermée : sans cette porte, l'autre script n'aurait eu qu'un
     seul recours, réécrire lui-même la forme de `hs_quote_v1`. Ce serait une seconde
     définition du format, et la clé est justement VERSIONNÉE — le jour où elle passe à
     `v: 2`, un duplicata oublié rendrait la liste illisible sans qu'aucun test ne le dise.

     On n'expose donc que ce qui est nécessaire : ajouter, et compter. Pas `write`, pas
     `clear`, pas `read` — un appelant extérieur n'a aucune raison de reconstruire la
     liste, et lui en donner le moyen ferait de ce format une API à maintenir.

     L'horodatage est posé ICI et non par l'appelant : c'est une propriété du stockage,
     pas de l'appel. */
  window.HSQuote = {
    add: function (variantId, handle, quantity, stamp) {
      if (!variantId || !handle) return false;
      return Store.add(variantId, handle, quantity, stamp || new Date().toISOString());
    },
    count: function () { return Store.count(); }
  };

  /* ==========================================================
     2. LA PASTILLE

     Écrite dans `[data-hs-quote-count]`, un attribut à NOUS. Le masquage à zéro
     est assuré par `hs-header-badge-js`, qui observe les mutations de texte et
     pose la classe `is-empty` — donc il suffit d'écrire le nombre ici.
     ========================================================== */

  var Badge = {
    refresh: function () {
      var n = Store.count();
      var cibles = document.querySelectorAll('[data-hs-quote-count]');
      Array.prototype.forEach.call(cibles, function (el) {
        el.textContent = n > 99 ? '99+' : String(n || '');
      });
    }
  };

  /* ==========================================================
     3. Bouton « Demander un devis » sur la fiche produit

     N'APPELLE PLUS /cart/add.js. C'est le geste exact qui a été inversé.
     ========================================================== */

  function variantEtQuantite(btn) {
    /* ------------------------------------------------------------------------------
       UN BOUTON QUI PORTE SA PROPRE DONNÉE NE CONSULTE PAS LE DOCUMENT — 01/08/2026.

       C'est la correction du défaut signalé par Yann : « le bouton Demander un devis sur
       la carte produit ne fonctionne pas ». Mesuré au navigateur sur DEV, et la cause
       n'était pas devinable au diff :

       - `document.querySelector('[data-product-handle]')` renvoyait **null** sur une page
         collection — il n'y en a aucun. `ctx.handle` restait donc vide, la garde plus bas
         sortait en silence, et `localStorage` restait vide. Vérifié : zéro octet écrit.
       - Pire si un handle avait existé : le repli `document.querySelector('form[...]')`
         prend le PREMIER formulaire de la page, donc celui d'un AUTRE produit, et sa
         valeur `[name="id"]` écrasait le `data-variant-id` du bouton cliqué. La carte
         numéro 12 aurait ajouté le produit de la carte numéro 1.

       Une carte de listing porte tout ce qu'il faut sur le bouton lui-même. On sort donc
       immédiatement, sans jamais interroger le document — la seule façon d'être juste
       quand vingt produits coexistent sur la page.
       ------------------------------------------------------------------------------ */
    var idBouton = btn.getAttribute('data-variant-id');
    var handleBouton = btn.getAttribute('data-product-handle');
    if (idBouton && handleBouton) {
      return { variantId: idBouton, quantity: 1, handle: handleBouton, autonome: true };
    }

    /* ------------------------------------------------------------------------------
       LA RECHERCHE EST BORNÉE À LA SECTION DU BOUTON — 03/10/2026.

       Défaut constaté en production : quel que soit le produit et la langue, la
       demande de devis affichait « Jeu de 5 chasses-goupilles SwissKH ». Cause : le
       commentaire d'origine supposait « il n'y a qu'un produit sur la page ». C'est
       faux depuis que le méga-menu de l'en-tête affiche des cartes produits SwissKH,
       chacune avec un `data-product-handle` et un formulaire `/cart/add`. L'en-tête
       précède la fiche dans le document, donc `document.querySelector(...)` renvoyait
       TOUJOURS la première carte du menu : jeu-de-5-chasses-goupilles-swisskh.

       La liste recevait alors le bon identifiant de variante associé au MAUVAIS
       handle ; `/products/<mauvais handle>?variant=<id étranger>` ignore la variante
       et rend la première variante des chasses-goupilles.

       Désormais :
         1. le handle vient du bouton lui-même (`data-hs-handle`, posé par
            `snippets/heidi-quote-button.liquid`) — on n'emploie PAS
            `data-product-handle`, qui ferait basculer le bouton dans le chemin
            « carte » ci-dessus (quantité 1, pas de redirection, variante figée) ;
         2. à défaut, formulaire et handle sont cherchés dans la section Shopify qui
            contient le bouton, jamais dans le document entier ;
         3. la page devis contrôle, à l'affichage, que la variante rendue est bien
            celle demandée (voir `rendre`), et répare sinon.
       ------------------------------------------------------------------------------ */
    var portee = btn.closest('.shopify-section') ||
                 btn.closest('[id^="shopify-section-"]') ||
                 btn.closest('main') ||
                 document;

    /* Ordre volontaire — `form[action*="/cart/add"]` en dernier, parce qu'il matcherait
       d'abord le formulaire `installment` de Shop Pay. */
    var form = btn.closest('form[data-type="add-to-cart-form"]') ||
               btn.closest('form[action*="/cart/add"]') ||
               portee.querySelector('form[data-type="add-to-cart-form"]') ||
               portee.querySelector('form[action*="/cart/add"]');

    var variantId = btn.getAttribute('data-variant-id') || null;
    var quantity = 1;

    if (form) {
      var idField = form.querySelector('[name="id"]');
      if (idField && idField.value) variantId = idField.value;

      /* CORRECTION : le formulaire contient un `quantity` CACHÉ, toujours à 1, en
         plus de celui que l'acheteur manipule. L'ancien code prenait le premier
         trouvé, donc le caché — la quantité saisie était ignorée. On préfère
         explicitement un champ visible. */
      var champs = form.querySelectorAll('[name="quantity"]');
      var choisi = null;
      Array.prototype.forEach.call(champs, function (c) {
        if (choisi) return;
        var cache = c.type === 'hidden' || (c.offsetParent === null && c.offsetHeight === 0);
        if (!cache) choisi = c;
      });
      if (!choisi && champs.length) choisi = champs[0];
      if (choisi && choisi.value) quantity = Math.max(1, parseInt(choisi.value, 10) || 1);
    }

    var handle = btn.getAttribute('data-hs-handle') || null;
    if (!handle) {
      /* Repli : jamais un bouton de carte (`[data-hs-quote-add]`), qui porte le
         handle d'un AUTRE produit, et jamais hors de la section du bouton. */
      var conteneur = portee.querySelector('[data-product-handle]:not([data-hs-quote-add])');
      if (conteneur) handle = conteneur.getAttribute('data-product-handle');
    }

    return { variantId: variantId, quantity: quantity, handle: handle };
  }

  function initProductButtons() {
    /* DÉLÉGATION AU DOCUMENT — 01/08/2026. C'était un écouteur par bouton, posé au
       démarrage sur `querySelectorAll('[data-hs-quote-add]')`. Sur la fiche produit il n'y
       a qu'un bouton et il vit jusqu'à la fin de la page, donc le défaut ne s'y voyait
       pas. Sur une grille de collection les cartes sont re-rendues en AJAX à chaque
       filtre, chaque tri et chaque page suivante, et slick clone ses diapositives : les
       écouteurs partaient avec l'ancien markup et les boutons devenaient muets, sans que
       rien ne le signale. */
    document.addEventListener('click', function (event) {
      var btn = event.target.closest ? event.target.closest('[data-hs-quote-add]') : null;
      if (!btn) return;
      {
        event.preventDefault();
        /* La carte est un lien : sans cela le clic ajoute ET navigue vers la fiche. */
        event.stopPropagation();
        if (btn.dataset.hsBusy === '1') return;

        var ctx = variantEtQuantite(btn);
        var label = btn.querySelector('.hs-quote-btn__label');

        /* CORRECTION : l'échec ne se déguise plus en succès. L'ancien code
           redirigeait vers la page devis dans tous les cas, y compris quand
           aucune variante n'était identifiable — l'acheteur arrivait sans son
           article et sans explication. */
        if (!ctx.variantId || !ctx.handle) {
          if (label) label.textContent = CONFIG.strings.updateError;
          return;
        }

        btn.dataset.hsBusy = '1';
        var texteOrigine = label ? label.textContent : null;
        if (label) label.textContent = CONFIG.strings.adding;

        var ok = Store.add(ctx.variantId, ctx.handle, ctx.quantity, new Date().toISOString());

        if (!ok) {
          if (label) label.textContent = CONFIG.strings.updateError;
          btn.dataset.hsBusy = '0';
          return;
        }

        /* Écriture locale, donc instantanée : pas de requête réseau, donc pas de
           `finally`. */
        if (label) label.textContent = CONFIG.strings.added;

        /* DEPUIS UNE CARTE, ON NE REDIRIGE PAS — 01/08/2026. Le renvoi vers la page devis
           est juste sur la fiche produit, où l'acheteur a fini de choisir. Depuis une
           grille il serait destructeur : il quitte le listing qu'il parcourait, et perd sa
           position après un geste qu'il croyait anodin. Le panneau de confirmation le
           renseigne sur place et lui laisse le lien.

           `hsNotice` est appelé sous garde : son fichier est chargé séparément, et une
           dépendance dure ferait échouer l'ajout entier s'il manquait. */
        if (ctx.autonome) {
          if (window.hsNotice) {
            window.hsNotice.montre({ type: 'quote', carte: btn.closest('.card') });
          }
        } else {
          window.setTimeout(function () {
            window.location.href = CONFIG.quoteUrl;
          }, 450);
        }

        window.setTimeout(function () {
          btn.dataset.hsBusy = '0';
          if (label && texteOrigine !== null) label.textContent = texteOrigine;
        }, 2000);
      }
    });
  }

  /* ==========================================================
     4. Transfert du panier vers la liste

     Arbitrage de Yann : le bouton devis de la page panier et du tiroir DÉPLACE le
     panier vers la liste, au lieu d'y mener simplement. C'est le cas réel — un
     acheteur remplit son panier, voit le total, puis décide de négocier. Sans
     transfert il devrait tout ressaisir.
     ========================================================== */

  function initTransfer() {
    /* DÉLÉGATION sur le document, et non un écouteur par bouton. Le panier tiroir
       est re-rendu en AJAX par Ella à chaque ajout : un écouteur posé à
       l'initialisation serait perdu avec l'ancien markup, et le bouton du tiroir
       deviendrait muet après le premier ajout au panier. Le défaut n'apparaîtrait
       qu'au deuxième essai, ce qui est le pire cas pour le repérer. */
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-hs-quote-transfer]');
      if (!btn) return;
      event.preventDefault();
      if (btn.dataset.hsBusy === '1') return;
      btn.dataset.hsBusy = '1';

      var label = btn.querySelector('.hs-quote-btn__label') || btn;
      var texteOrigine = label.textContent;
      label.textContent = CONFIG.strings.adding;

      /* On relit le panier au moment du clic plutôt que de se fier au DOM : la
         page panier peut avoir été modifiée en AJAX depuis son rendu. */
      fetch(CONFIG.routes.cartJs, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var stamp = new Date().toISOString();
          (cart.items || []).forEach(function (item) {
            Store.add(item.variant_id, item.handle, item.quantity, stamp);
          });

          /* Le panier est vidé : c'est un DÉPLACEMENT, pas une copie. Laisser les
             articles dans les deux endroits recréerait exactement la confusion
             qu'on vient de supprimer. */
          if (!cart.items || !cart.items.length) return null;
          return postJSON(CONFIG.routes.cartClear, {});
        })
        .then(function () {
          label.textContent = CONFIG.strings.transferDone;
          window.location.href = CONFIG.quoteUrl;
        })
        .catch(function () {
          label.textContent = CONFIG.strings.updateError;
          btn.dataset.hsBusy = '0';
          window.setTimeout(function () { label.textContent = texteOrigine; }, 2500);
        });
    });
  }

  /* ==========================================================
     5. Page devis — rendu de la liste, quantités, total

     Chaque ligne est demandée au SERVEUR par
     `/products/<handle>?variant=<id>&view=hs_quote_line`.
     Voir `templates/product.hs_quote_line.liquid` pour la raison : la ligne a
     besoin de metafields et d'un prix converti, ni l'un ni l'autre n'étant
     accessibles depuis le JSON produit.
     ========================================================== */

  function urlLigne(item) {
    return CONFIG.routes.root.replace(/\/$/, '') +
           '/products/' + encodeURIComponent(item.h) +
           '?variant=' + encodeURIComponent(item.id) +
           '&view=hs_quote_line';
  }

  /* Rendu serveur d'une ligne, réduit à son `<li>`. `null` si le serveur ne rend
     rien d'exploitable. */
  function chargerLigne(item) {
    return fetch(urlLigne(item), { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var tmp = document.createElement('tbody');
        tmp.innerHTML = html.trim();
        return tmp.querySelector('[data-hs-quote-line]');
      });
  }

  /* Retrouve le VRAI handle d'une variante par `/variants/<id>`, qui redirige vers
     `/products/<handle>?variant=<id>`. `HEAD` suffit : seule l'URL finale compte,
     inutile de télécharger la fiche. Résout `null` si la variante n'existe plus. */
  function resoudreHandle(variantId) {
    var url = CONFIG.routes.root.replace(/\/$/, '') +
              '/variants/' + encodeURIComponent(variantId);
    return fetch(url, { method: 'HEAD', redirect: 'follow' })
      .then(function (r) {
        if (!r.ok) return null;
        var m = /\/products\/([^\/?#]+)/.exec(r.url || '');
        if (!m) return null;
        try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
      })
      .catch(function () { return null; });
  }

  function ligneIndisponible(item) {
    /* Une référence renommée ou dépubliée dans l'admin donne un 404. Elle ne
       disparaît PAS en silence : l'acheteur doit savoir ce qu'il a perdu, et
       pouvoir la retirer. */
    var li = document.createElement('li');
    li.className = 'hs-quote__line hs-quote__line--unavailable';
    li.setAttribute('data-hs-quote-line', '');
    li.setAttribute('data-variant-id', item.id);
    li.setAttribute('data-quantity', item.q);
    li.setAttribute('data-unit-price', '0');
    li.setAttribute('data-sku', '—');
    li.setAttribute('data-title', CONFIG.strings.unavailable);
    li.setAttribute('data-vendor', '');
    li.innerHTML =
      '<span class="hs-quote__thumb" aria-hidden="true"></span>' +
      '<div class="hs-quote__line-main"><p class="hs-quote__line-meta">' +
      CONFIG.strings.unavailable +
      '</p></div>' +
      '<div class="hs-quote__line-side">' +
      '<button type="button" class="hs-quote__remove" data-hs-quote-remove></button>' +
      '</div>';
    li.querySelector('[data-hs-quote-remove]').textContent = CONFIG.strings.removeLine;
    return li;
  }

  function initQuotePage() {
    var root = document.querySelector('[data-hs-quote-root]');
    if (!root) return;

    var liste = root.querySelector('[data-hs-quote-list]');
    var payloadField = root.querySelector('[data-hs-quote-payload]');
    var totalValue = root.querySelector('[data-hs-quote-total]');
    var countEl = root.querySelector('[data-hs-quote-count]');
    /* Le champ caché du formulaire, celui qui part réellement dans l'e-mail. Il est
       dans l'autre colonne mais sous la même racine — `data-hs-quote-root` est posé
       sur le conteneur de la section, qui enveloppe les deux panneaux. */
    var totalField = root.querySelector('[data-hs-quote-total-field]');
    var resume = root.querySelector('[data-hs-quote-summary]');
    var resumeCount = root.querySelector('[data-hs-quote-sum-count]');
    var resumeTotal = root.querySelector('[data-hs-quote-sum-total]');
    var vide = root.querySelector('[data-hs-quote-empty]');
    var corps = root.querySelector('[data-hs-quote-body]');

    /* Envoi réussi : la section rend ce marqueur quand `form.posted_successfully?`.
       On vide alors la liste — sans quoi l'acheteur qui revient croit sa demande
       non partie, ou la renvoie en double. Arbitrage de Yann. */
    if (root.querySelector('[data-hs-quote-sent]')) {
      Store.clear();
      return;
    }

    function totaux() {
      var lignes = root.querySelectorAll('[data-hs-quote-line]');
      var total = 0;
      var nb = 0;

      Array.prototype.forEach.call(lignes, function (line) {
        var input = line.querySelector('[data-hs-qty-input]');
        var q = Math.max(0, parseInt(input ? input.value : line.getAttribute('data-quantity'), 10) || 0);
        var pu = parseInt(line.getAttribute('data-unit-price'), 10) || 0;
        var prix = line.querySelector('[data-hs-line-price]');
        if (prix) prix.textContent = money(pu * q);
        total += pu * q;
        nb += q;
      });

      if (totalValue) totalValue.textContent = money(total);

      /* LE CHAMP CACHÉ ENVOYAIT LE TOTAL DU PANIER SHOPIFY, PAS CELUI DU DEVIS.
         Corrigé le 02/08/2026. La section l'initialisait à
         `{{ cart.total_price | money_with_currency }}` — un reliquat du modèle
         abandonné le 30/07, où la demande transitait par le panier. Rien ne le
         remettait à jour, donc l'équipe recevait un montant sans rapport avec la
         demande, et ça ne se voyait NULLE PART à l'écran. */
      if (totalField) totalField.value = money(total);

      /* Vrai pluriel, et non « article(s) ». Le JS ne sait pas résoudre une forme
         plurielle Liquid : il reçoit les deux chaînes et choisit. */
      if (countEl) {
        var gabarit = nb === 1 ? CONFIG.strings.itemCountOne : CONFIG.strings.itemCountOther;
        countEl.textContent = String(gabarit || '').replace('[count]', nb);
      }

      /* Le résumé replié du doigt reprend les deux mêmes valeurs, pour n'avoir
         qu'une seule source de vérité. */
      if (resumeCount) resumeCount.textContent = countEl ? countEl.textContent : '';
      if (resumeTotal) resumeTotal.textContent = money(total);
    }

    function payload() {
      if (!payloadField) return;
      var lignes = root.querySelectorAll('[data-hs-quote-line]');
      var rows = [];

      Array.prototype.forEach.call(lignes, function (line) {
        var input = line.querySelector('[data-hs-qty-input]');
        var q = input ? input.value : line.getAttribute('data-quantity');
        var sku = line.getAttribute('data-sku') || '—';
        var titre = line.getAttribute('data-title') || '';
        var vendeur = line.getAttribute('data-vendor') || '';
        rows.push(q + ' x  [' + sku + ']  ' + titre + (vendeur ? '  (' + vendeur + ')' : ''));
      });

      payloadField.value = rows.length ? rows.join('\n') : CONFIG.strings.emptyPayload;
    }

    function etatVide() {
      var reste = root.querySelectorAll('[data-hs-quote-line]').length;
      if (vide) vide.hidden = reste > 0;
      if (corps) corps.hidden = reste === 0;
      replier(reste);
    }

    /* ----------------------------------------------------------------------- *
     * RÉSUMÉ REPLIÉ AU DOIGT. Arbitrage de Yann du 02/08/2026 : mesurée à 390 px,
     * la page fait 3 389 px et le bouton d'envoi tombe au troisième écran.
     *
     * Le repli est PILOTÉ ICI ET NON EN CSS, pour deux raisons. La sélection n'est
     * rendue que par ce script : sans lui il n'y a rien à replier, donc un bouton
     * posé par le CSS serait un bouton inerte — d'où le `hidden` du markup, levé
     * seulement quand la liste existe. Et le seuil doit être le même que celui de
     * la grille : le lire dans le CSS aurait été deux vérités à tenir d'accord.
     *
     * Le seuil de 1025 px est celui du thème entier, pas une valeur inventée.
     * ----------------------------------------------------------------------- */
    function auDoigt() {
      return window.matchMedia('(max-width: 1024.98px)').matches;
    }

    function replier(nbLignes) {
      if (!resume || !corps) return;
      var pertinent = auDoigt() && nbLignes > 0;
      resume.hidden = !pertinent;
      if (!pertinent) {
        /* Au clavier, le panneau est toujours ouvert et le bouton n'existe pas.
           On ne laisse jamais `corps` masqué par le repli : l'état vide, lui, est
           géré par `etatVide` juste au-dessus. */
        if (nbLignes > 0) corps.hidden = false;
        return;
      }
      var ouvert = resume.getAttribute('aria-expanded') === 'true';
      corps.hidden = !ouvert;
    }

    if (resume && corps) {
      resume.addEventListener('click', function () {
        var ouvert = resume.getAttribute('aria-expanded') === 'true';
        resume.setAttribute('aria-expanded', ouvert ? 'false' : 'true');
        corps.hidden = ouvert;
      });
      /* Le passage d'un palier à l'autre — rotation d'un téléphone, fenêtre
         redimensionnée — doit rendre le panneau au clavier, sinon il resterait
         masqué par un repli devenu invisible. */
      window.addEventListener('resize', function () {
        replier(root.querySelectorAll('[data-hs-quote-line]').length);
      });
    }

    function rendre() {
      if (!liste) return Promise.resolve();
      var items = Store.read().items;

      if (!items.length) {
        liste.innerHTML = '';
        etatVide();
        totaux();
        payload();
        return Promise.resolve();
      }

      liste.setAttribute('aria-busy', 'true');

      /* Requêtes en parallèle, ordre rétabli ensuite : une liste de devis se relit
         comme une suite de décisions, la voir se réordonner est désorientant. */
      /* CONTRÔLE DE LA VARIANTE RENDUE — 03/10/2026.

         `templates/product.hs_quote_line.liquid` retombe sur la première variante
         du produit quand l'identifiant demandé ne lui appartient pas. Son commentaire
         annonçait que « le JS compare l'identifiant rendu à celui qu'il a demandé » :
         ce contrôle n'avait jamais été écrit. C'est lui qui aurait rendu visible le
         défaut des chasses-goupilles au lieu de l'afficher comme une ligne valide.

         En cas d'écart, on interroge `/variants/<id>` pour connaître le vrai handle,
         on recharge la ligne, et on corrige le magasin : les listes enregistrées
         avant le correctif se réparent ainsi d'elles-mêmes à la première visite. Si
         la variante n'existe plus, la ligne est signalée indisponible — jamais
         remplacée par un autre article. */
      function memeVariante(li, item) {
        return !!li && String(li.getAttribute('data-variant-id')) === String(item.id);
      }

      return Promise.all(items.map(function (item) {
        return chargerLigne(item)
          .catch(function () { return null; })
          .then(function (li) {
            if (memeVariante(li, item)) return { item: item, li: li };

            return resoudreHandle(item.id).then(function (handle) {
              if (!handle || handle === item.h) return { item: item, li: null };
              var corrige = { id: item.id, h: handle, q: item.q };
              return chargerLigne(corrige).then(function (li2) {
                if (!memeVariante(li2, item)) return { item: item, li: null };
                Store.setHandle(item.id, handle);
                return { item: item, li: li2 };
              });
            });
          })
          .catch(function () { return { item: item, li: null }; });
      })).then(function (resultats) {
        liste.innerHTML = '';

        resultats.forEach(function (res) {
          var li = res.li;
          if (!li) {
            liste.appendChild(ligneIndisponible(res.item));
            return;
          }
          /* La quantité vient du magasin, pas du serveur : Liquid ne peut pas la
             connaître, le gabarit rend donc toujours 1. */
          li.setAttribute('data-quantity', res.item.q);
          var input = li.querySelector('[data-hs-qty-input]');
          if (input) input.value = res.item.q;
          liste.appendChild(li);
        });

        liste.removeAttribute('aria-busy');
        etatVide();
        totaux();
        payload();
      });
    }

    /* Steppers — délégation, un seul écouteur. Ils écrivent dans le magasin, plus
       dans /cart/change.js. Plus d'index de ligne positionnel non plus : la clé
       est l'identifiant de variante, stable quoi qu'il arrive à la liste. */
    root.addEventListener('click', function (event) {
      var pas = event.target.closest('[data-hs-qty-step]');
      if (pas) {
        event.preventDefault();
        var ligne = pas.closest('[data-hs-quote-line]');
        var input = ligne && ligne.querySelector('[data-hs-qty-input]');
        if (!input) return;

        var delta = pas.getAttribute('data-hs-qty-step') === 'up' ? 1 : -1;
        var suivant = Math.max(0, (parseInt(input.value, 10) || 1) + delta);
        var id = ligne.getAttribute('data-variant-id');

        if (suivant === 0) {
          Store.remove(id);
          ligne.remove();
        } else {
          input.value = suivant;
          Store.setQty(id, suivant);
        }
        totaux();
        payload();
        etatVide();
        return;
      }

      var retrait = event.target.closest('[data-hs-quote-remove]');
      if (retrait) {
        event.preventDefault();
        var l = retrait.closest('[data-hs-quote-line]');
        if (!l) return;
        Store.remove(l.getAttribute('data-variant-id'));
        l.remove();
        totaux();
        payload();
        etatVide();
      }
    });

    root.addEventListener('change', function (event) {
      var input = event.target.closest('[data-hs-qty-input]');
      if (!input) return;
      var ligne = input.closest('[data-hs-quote-line]');
      if (!ligne) return;
      var suivant = Math.max(0, parseInt(input.value, 10) || 0);
      var id = ligne.getAttribute('data-variant-id');

      if (suivant === 0) {
        Store.remove(id);
        ligne.remove();
      } else {
        input.value = suivant;
        Store.setQty(id, suivant);
      }
      totaux();
      payload();
      etatVide();
    });

    rendre();
    initValidation(root);
  }

  /* ==========================================================
     6. Validation — messages en ligne, pas d'alert() natif
        Inchangée par le détachement.
     ========================================================== */

  function initValidation(root) {
    var form = root.querySelector('[data-hs-quote-form]') ||
               root.querySelector('form.hs-quote__form');
    if (!form) return;

    function fieldError(field, message) {
      var wrap = field.closest('.hs-quote__field');
      var slot = wrap && wrap.querySelector('[data-hs-field-error]');
      if (slot) slot.textContent = message || '';
      if (message) {
        field.setAttribute('aria-invalid', 'true');
      } else {
        field.removeAttribute('aria-invalid');
      }
    }

    function validateField(field) {
      var value = (field.value || '').trim();

      if (field.hasAttribute('required') && !value) {
        fieldError(field, CONFIG.strings.required);
        return false;
      }
      if (field.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
        fieldError(field, CONFIG.strings.invalidEmail);
        return false;
      }
      fieldError(field, '');
      return true;
    }

    Array.prototype.forEach.call(
      form.querySelectorAll('.hs-quote__input, .hs-quote__select, .hs-quote__textarea'),
      function (field) {
        field.addEventListener('blur', function () { validateField(field); });
        field.addEventListener('input', function () {
          if (field.getAttribute('aria-invalid') === 'true') validateField(field);
        });
      }
    );

    form.addEventListener('submit', function (event) {
      var fields = form.querySelectorAll('[required]');
      var firstInvalid = null;

      Array.prototype.forEach.call(fields, function (field) {
        if (!validateField(field) && !firstInvalid) firstInvalid = field;
      });

      if (firstInvalid) {
        event.preventDefault();
        firstInvalid.focus();
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      var submit = form.querySelector('.hs-quote__submit');
      if (submit) {
        submit.disabled = true;
        submit.textContent = CONFIG.strings.sending;
      }
    });
  }

  /* ========================================================== */

  function init() {
    Badge.refresh();
    initProductButtons();
    initTransfer();
    initQuotePage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();